/**
 * LiveInsightsPanel — sticky right-side panel that shows key derived metrics
 * as the user fills in the Business Calculator form.
 *
 * Uses local formulas that mirror the backend so numbers appear instantly.
 * Falls back to a friendly empty state when the form is too sparse.
 *
 * Design borrows from the NexTruck "Fleet insights" side panel: soft radial
 * gauge, headline metrics, and a compact table.
 */
import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';

const currencySymbol = (code) => ({ USD: '$', CAD: '$', GBP: '£', EUR: '€' })[code] || '$';

const LiveInsightsPanel = ({ formData = {}, outputs = null, currency = 'USD', testId = 'live-insights-panel' }) => {
  const sym = currencySymbol(currency);

  // Local mirror of the key backend math so users see numbers as they type.
  const local = useMemo(() => {
    const n = (v) => parseFloat(v) || 0;
    const units = n(formData.units_per_batch);
    const price = n(formData.selling_price);
    const revenue = units * price;

    const ingredient = n(formData.ingredient_cost);
    const packaging = n(formData.packaging_cost)
      || (n(formData.packaging_cost_per_unit) * units)
      || (n(formData.packaging_bag_cost) + n(formData.packaging_label_cost) + n(formData.packaging_o2_cost) + n(formData.packaging_seal_cost) + n(formData.packaging_other_cost)) * units;
    const labor = n(formData.labor_hours) * n(formData.labor_rate);
    const electricity = n(formData.electricity_cost)
      || n(formData.electricity_rate_kwh) * n(formData.machine_power_kw) * n(formData.cycle_time_hours);
    const consumables = n(formData.consumables_cost)
      || (n(formData.consumable_pump_oil) + n(formData.consumable_tray_liners) + n(formData.consumable_cleaning) + n(formData.consumable_other));
    const wastePct = n(formData.waste_loss);
    const wasteLoss = (ingredient + packaging) * (wastePct / 100);
    const directCost = ingredient + packaging + labor + electricity + consumables + wasteLoss;

    // Simplified overhead per batch
    const opex = n(formData.monthly_rent) + n(formData.monthly_utilities) + n(formData.monthly_water)
      + n(formData.monthly_gas) + n(formData.monthly_insurance) + n(formData.monthly_software)
      + n(formData.monthly_marketing) + n(formData.monthly_maintenance) + n(formData.monthly_supplies)
      + n(formData.monthly_professional_services) + n(formData.other_overhead);
    const batchesPerMonth = n(formData.batches_per_month) || 1;
    const overheadPerBatch = opex / batchesPerMonth;

    const totalCost = directCost + overheadPerBatch;
    const costPerUnit = units > 0 ? totalCost / units : 0;
    const grossProfitPerBatch = revenue - directCost;
    const netProfitPerBatch = revenue - totalCost;
    const netMarginPct = revenue > 0 ? (netProfitPerBatch / revenue) * 100 : 0;
    const monthlyRevenue = revenue * batchesPerMonth;
    const monthlyProfit = netProfitPerBatch * batchesPerMonth;
    const breakevenUnits = (price - (directCost / (units || 1))) > 0
      ? Math.ceil(overheadPerBatch / (price - (directCost / (units || 1))))
      : null;

    return {
      units, price, revenue,
      directCost, overheadPerBatch, totalCost, costPerUnit,
      grossProfitPerBatch, netProfitPerBatch, netMarginPct,
      monthlyRevenue, monthlyProfit, breakevenUnits,
    };
  }, [formData]);

  // Prefer server-verified outputs when available; local mirror is the fallback
  const view = outputs
    ? {
        costPerUnit: parseFloat(outputs.cost_per_unit) || 0,
        netMarginPct: parseFloat(outputs.net_margin_percent) || 0,
        monthlyProfit: parseFloat(outputs.monthly_profit) || 0,
        breakevenUnits: parseFloat(outputs.breakeven_units) || null,
        totalCost: parseFloat(outputs.total_cost_per_batch) || 0,
        revenue: parseFloat(outputs.total_revenue) || (local.revenue),
        source: 'server',
      }
    : { ...local, source: 'local' };

  const fmt = (v, opts = {}) => {
    if (v === null || v === undefined || isNaN(v)) return '—';
    return v.toLocaleString(undefined, { maximumFractionDigits: opts.d ?? 2 });
  };

  const enoughInputs = local.units > 0 && local.price > 0 && local.directCost > 0;

  // Ring visual — arc fill based on margin (clamped -30..70 => 0..100%)
  const clampedMargin = Math.max(-30, Math.min(70, view.netMarginPct || 0));
  const marginRingPct = ((clampedMargin + 30) / 100) * 100;

  return (
    <aside
      className="pro-card p-5 sm:p-6 rounded-3xl"
      data-testid={testId}
      aria-label="Live financial insights"
    >
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading text-lg font-extrabold tracking-tight">Live insights</h3>
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          {view.source === 'server' ? 'Server' : 'Live preview'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        {enoughInputs || view.source === 'server'
          ? 'Numbers update as you type. Hit Calculate for the full financial report.'
          : 'Fill in the form to see live insights.'}
      </p>

      {/* Headline metric — big net profit / month */}
      <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 mb-5 relative overflow-hidden">
        <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Projected monthly net</div>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          {enoughInputs || view.source === 'server' ? (
            <span className={`font-heading text-3xl sm:text-4xl font-extrabold tabular-nums ${view.monthlyProfit >= 0 ? 'text-foreground' : 'text-red-600 dark:text-red-400'}`} data-testid="live-monthly-profit">
              {view.monthlyProfit < 0 ? '-' : ''}{sym}{fmt(Math.abs(view.monthlyProfit), { d: 0 })}
            </span>
          ) : (
            <span className="font-heading text-3xl sm:text-4xl font-extrabold tabular-nums text-foreground/40" data-testid="live-monthly-profit">
              {sym}—
            </span>
          )}
          <span className="text-xs text-muted-foreground">/ month</span>
        </div>
        {enoughInputs || view.source === 'server' ? (
          <>
            <div className="mt-2 flex items-center gap-1.5">
              {view.netMarginPct >= 0 ? (
                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5 text-red-500" />
              )}
              <span className="text-xs font-semibold text-foreground/80">
                {fmt(view.netMarginPct, { d: 1 })}% net margin
              </span>
            </div>
            <div className="absolute inset-x-4 bottom-2 h-1 rounded-full bg-primary/15 overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${marginRingPct}%` }} />
            </div>
          </>
        ) : (
          <div className="mt-2 text-xs text-muted-foreground">Add a few more numbers below to unlock the projection.</div>
        )}
      </div>

      {/* Metrics table */}
      <div className="space-y-2.5">
        <InsightRow label="Cost / unit" value={enoughInputs || view.source === 'server' ? `${sym}${fmt(view.costPerUnit)}` : '—'} testId="live-cost-per-unit" />
        <InsightRow label="Revenue / batch" value={local.revenue > 0 || view.source === 'server' ? `${sym}${fmt(view.revenue, { d: 0 })}` : '—'} testId="live-revenue" />
        <InsightRow label="Total cost / batch" value={enoughInputs || view.source === 'server' ? `${sym}${fmt(view.totalCost, { d: 0 })}` : '—'} testId="live-total-cost" />
        <InsightRow
          label="Break-even units"
          value={(enoughInputs || view.source === 'server') && view.breakevenUnits ? `${fmt(view.breakevenUnits, { d: 0 })} u` : '—'}
          testId="live-breakeven"
        />
      </div>

      {!enoughInputs && view.source !== 'server' && (
        <div className="mt-5 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
          <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <span>Add <strong className="text-foreground">units per batch</strong>, <strong className="text-foreground">selling price</strong>, and at least one direct cost to unlock the preview.</span>
        </div>
      )}
    </aside>
  );
};

const InsightRow = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-3 pb-2 border-b border-border/60 last:border-b-0 last:pb-0">
    <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</span>
    <span className="font-heading text-sm font-extrabold tabular-nums text-foreground" data-testid={testId}>
      {value}
    </span>
  </div>
);

export default LiveInsightsPanel;
